// Quantum Information module (WASM-powered)
import init, {
  get_quantum_defaults, run_fidelity_scan, run_errorcorrection_scan,
  run_entanglement_scan, run_decoherence_scan, run_circuit_scan
} from './engines.js';
import { getLicense, hasFullAccess } from './auth.js';
import { GeoNum, geo } from './geo-num.js';
import { ZONES_QUANTUM } from './geo-num-zones.js';
import { registerReport } from './report-gen.js';

const _MODULE = 'Quantum';
const charts = {};
let ready = false, defaults = null;

// Reference experimental device specifications from literature
const QUANTUM_REFS = {
  ibm_eagle: {
    name: 'IBM Eagle (127Q)', qubits: 127,
    t1_us: 300, t2_us: 200,
    f1q: 0.999, f2q: 0.995, qv: 64,  // QV ≠ qubit count; published QV=64
    source: 'IBM Quantum', url: 'https://quantum.ibm.com/'
  },
  ibm_heron: {
    name: 'IBM Heron (133Q)', qubits: 133,
    t1_us: 350, t2_us: 250,
    f1q: 0.9995, f2q: 0.997, qv: null,
    source: 'IBM Quantum', url: 'https://quantum.ibm.com/'
  },
  google_sycamore: {
    name: 'Google Sycamore (53Q)', qubits: 53,
    t1_us: 16, t2_us: 10,  // median T1≈15.6µs per Nature 574, 505 (2019)
    f1q: 0.9985, f2q: 0.994, qv: null,
    source: 'Google AI Quantum', url: 'https://quantumai.google/'
  },
  chsh: {
    classical_limit: 2.0,
    quantum_max: 2.828, // 2*sqrt(2)
    typical_exp: 2.7,
    source: 'Clauser, Horne, Shimony, Holt (1969)', url: 'https://doi.org/10.1103/PhysRevLett.23.880'
  },
  /* The necessary AND sufficient condition for a mixed two-qubit state to
     violate CHSH. Quoted here because the entanglement panel reports a Werner
     (depolarized Bell) state, for which the condition reduces to a single
     threshold on the visibility, and a reader comparing a reported S against
     the 2.0 line above needs to know which quantity that S is built from. */
  horodecki: {
    v_threshold: 0.70710678,        // 1/sqrt(2)
    f_threshold: 0.78033009,        // (1 + 3/sqrt(2))/4, the same threshold in fidelity
    source: 'Horodecki, Horodecki & Horodecki, Phys. Lett. A 200 (1995) 340',
    url: 'https://doi.org/10.1016/0375-9601(95)00214-N'
  }
};

function qRefLink(ref) {
  return `<a href="${ref.url}" target="_blank" style="color:var(--accent,#4fc3f7);text-decoration:none;">${ref.source}</a>`;
}

function renderQuantumDeviceRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const devs = [QUANTUM_REFS.ibm_eagle, QUANTUM_REFS.ibm_heron, QUANTUM_REFS.google_sycamore];
  let rows = '';
  for (const d of devs) {
    rows += `<tr>
      <td style="padding:2px 8px;color:var(--text-primary);">${d.name}</td>
      <td style="padding:2px 8px;">${d.t1_us}\u00B5s</td>
      <td style="padding:2px 8px;">${d.t2_us}\u00B5s</td>
      <td style="padding:2px 8px;">${(d.f1q * 100).toFixed(2)}%</td>
      <td style="padding:2px 8px;">${(d.f2q * 100).toFixed(1)}%</td>
      <td style="padding:2px 8px;">${d.qv ?? '\u2014'}</td>
      <td style="padding:2px 8px;">${qRefLink(d)}</td>
    </tr>`;
  }
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:var(--bg-alt);border:1px solid var(--border);border-radius:4px;font-size:11px;color:var(--text-secondary);line-height:1.6;">
    <strong style="color:var(--accent,#4fc3f7);">Reference Device Specifications</strong>
    <table style="margin-top:4px;border-collapse:collapse;width:100%;font-size:11px;color:var(--text-secondary);">
      <thead><tr style="border-bottom:1px solid var(--border);">
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">Device</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">T1</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">T2</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">1Q F</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">2Q F</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">QV</th>
        <th style="padding:2px 8px;text-align:left;color:var(--text-secondary);">Source</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`;
}

function renderCHSHRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const c = QUANTUM_REFS.chsh;
  const h = QUANTUM_REFS.horodecki;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:var(--bg-alt);border:1px solid var(--border);border-radius:4px;font-size:11px;color:var(--text-secondary);line-height:1.7;">
    <strong style="color:var(--accent,#4fc3f7);">CHSH / Bell Inequality Reference</strong><br>
    Classical limit: <strong>S \u2264 ${c.classical_limit.toFixed(1)}</strong> &nbsp;|&nbsp;
    Quantum max (Tsirelson bound): <strong>2\u221A2 \u2248 ${c.quantum_max.toFixed(3)}</strong> &nbsp;|&nbsp;
    Typical experimental: <strong>\u2248${c.typical_exp}</strong>
    (${qRefLink(c)})<br>
    This panel models a depolarized Bell pair (Werner state)
    \u03C1(v) = v|\u03A8\u27E9\u27E8\u03A8| + (1-v)I/4, so S = 2\u221A2 v and the
    reported fidelity is F = (1+3v)/4. Violation requires
    <strong>v &gt; 1/\u221A2 \u2248 ${h.v_threshold.toFixed(4)}</strong>, that is
    <strong>F &gt; ${h.f_threshold.toFixed(4)}</strong> - not F &gt; 1/\u221A2. A pair with
    v between 1/3 and 1/\u221A2 is still entangled but violates no CHSH inequality.
    (${qRefLink(h)})
  </div>`;
}

// Animation state
const animData = {};

export async function initWasm() {
  try {
    await init();
    ready = true;
    defaults = JSON.parse(get_quantum_defaults());
    populateDefaults();
    // Configure GeoNum for quantum domain (eV-scale precision)
    GeoNum.setZoneConfig(ZONES_QUANTUM.boundaries, ZONES_QUANTUM.minZone);
  }
  catch (err) { console.error('WASM init (quantum):', err); }
}

export function init_ui() {
  document.querySelectorAll('.qtab').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.qtab').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.quantum-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById('qtp-' + btn.dataset.qt).classList.add('active');
    });
  });
  bind('btn-fidelity', fidelityScan);
  bind('btn-errorcorrect', errorcorrectScan);
  bind('btn-entanglement', entanglementScan);
  bind('btn-decoherence', decoherenceScan);
  bind('btn-circuit', circuitScan);
}

export function applyQuantumGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const has = hasFullAccess(tier) || license?.modules?.includes('quantum') || (tier === 'trial' && license?.isActive);
  const g = document.getElementById('qt-gate');
  const c = document.getElementById('qt-content');
  if (g && c) {
    if (has) { g.classList.add('hidden'); c.classList.remove('hidden'); }
    else { g.classList.remove('hidden'); c.classList.add('hidden'); }
  }
}

function populateDefaults() {
  if (!defaults) return;
  setTA('in-fidelity', defaults.fidelity?.qubits);
  setTA('in-errorcorrect', defaults.errorcorrection?.codes);
  setTA('in-entanglement', defaults.entanglement?.pairs);
  setTA('in-decoherence', defaults.decoherence?.qubits);
  setTA('in-circuit', defaults.circuit?.hardware);
}

function setTA(id, v) { const el = document.getElementById(id); if (el && v) el.value = JSON.stringify(v, null, 2); }
function bind(id, fn) { const el = document.getElementById(id); if (el) el.addEventListener('click', async () => { if (!ready) return; el.disabled = true; window.gdbsShowLoading && window.gdbsShowLoading('Computing...', (el.textContent || '').trim()); await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r))); try { await fn(); } finally { window.gdbsHideLoading && window.gdbsHideLoading(); el.disabled = false; } }); }
function status(id, m) { const el = document.getElementById('s-' + id); if (el) el.textContent = m; }
function fmtSci(v) { return typeof v === 'number' ? (Math.abs(v) > 1e6 || (Math.abs(v) < 0.001 && v !== 0) ? v.toExponential(3) : v.toFixed(4)) : v; }
function tierColor(val) {
  const v = parseFloat(val); let r, g, b;
  if (v < 0.5) { const t = v / 0.5; r = 220; g = Math.round(50 + 170 * t); b = 50; }
  else { const t = (v - 0.5) / 0.5; r = Math.round(220 - 180 * t); g = 200; b = Math.round(50 + 30 * t); }
  return `rgb(${r},${g},${b})`;
}
function renderGradientBar(tc) {
  if (!tc) return '';
  const base = [{ val: tc.core, name: 'Core' }, { val: tc.magnitude, name: 'Mag' }, { val: tc.phase, name: 'Phase' }, { val: tc.proportion, name: 'Prop' }];
  const rel = [];
  if (tc.relational) { for (let i = 0; i < tc.relational.length; i++) { const r = tc.relational[i], l = i + 2; rel.push({ val: r[0], name: `Mag\u00D7${l}` }, { val: r[1], name: `Phase\u00D7${l}` }, { val: r[2], name: `Prop\u00D7${l}` }); } }
  const all = [...base, ...rel], pct = (100 / all.length).toFixed(2);
  let h = '<div class="tc-wrap"><div class="tc-bar">';
  for (const t of all) h += `<div class="tc-seg" style="width:${pct}%;background:${tierColor(Math.max(0.08, t.val))}"><span class="tc-tip">${t.name}<br>${t.val.toFixed(3)}</span></div>`;
  h += '</div><div class="tc-labels">'; for (const t of all) h += `<span class="tc-lbl" style="width:${pct}%">${t.val.toFixed(2)}</span>`;
  return h + '</div></div>';
}
function renderTable(id, headers, rows) {
  let h = '<thead><tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr></thead><tbody>';
  for (const r of rows) h += '<tr>' + r.map(v => '<td>' + fmtSci(v) + '</td>').join('') + '</tr>';
  const el = document.getElementById('t-' + id); if (el) el.innerHTML = h + '</tbody>';
}
function makeChart(id, labels, datasets, xLbl, yLbl, type) {
  if (charts[id]) charts[id].destroy();
  const c = document.getElementById('c-' + id); if (!c) return;
  charts[id] = new Chart(c.getContext('2d'), { type: type || 'bar', data: { labels, datasets },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      scales: { x: { title: { display: true, text: xLbl } }, y: { title: { display: true, text: yLbl } } },
      plugins: { legend: { position: 'top' } } } });
  try { const _s=datasets.map(ds=>({label:ds.label??'Series',color:ds.borderColor??(typeof ds.backgroundColor==='string'?ds.backgroundColor:'#2563eb'),dashed:!!(ds.borderDash&&ds.borderDash.length),data:labels?labels.map((l,i)=>{const v=ds.data[i];return[typeof l==='number'?l:i,v!=null&&typeof v==='object'?(v.y??0):(v??0)];}):ds.data.map(p=>Array.isArray(p)?p:(p&&typeof p==='object'?[p.x??0,p.y??0]:[0,p??0]))}));registerReport({title:id,module:_MODULE,params:[],series:_s,xLabel:xLbl,yLabel:yLbl});} catch(_){}
}

// --- 3D viewport adapter ---
// Each scan emits the RICHEST genuine field it computes into the shared WebGPU
// viewport as window.__gdbs3dPayload = { points, values, title }. The viewport
// per-axis normalizes geometry, so real (unscaled) numbers are passed directly.
// Guarded so nothing breaks if gdbs3D is absent. set3DScatter remains as a
// sparse multi-field fallback for genuinely scalar-only results.
function setPayload(points, values, title) {
  try {
    if (!points || !points.length) return;
    window.__gdbs3dPayload = { points: new Float32Array(points), values, title };
  } catch (_) {}
}

function set3DScatter(rows, primaryKey, secondaryKey, title) {
  try {
    if (!Array.isArray(rows) || !rows.length) return;
    const points = [], values = [];
    for (let i = 0; i < rows.length; i++) {
      const p = Number(rows[i]?.[primaryKey]);
      const s = secondaryKey != null ? Number(rows[i]?.[secondaryKey]) : 0;
      if (!isFinite(p)) continue;
      points.push(i, isFinite(p) ? p : 0, isFinite(s) ? s : 0);
      values.push(isFinite(p) ? p : 0);
    }
    if (!points.length) return;
    window.__gdbs3dPayload = { points: new Float32Array(points), values, title };
  } catch (_) {}
}
// Fidelity: each device's circuit fidelity F(n) = single_f^n * readout_f is a
// genuine closed-form decay curve. Plot one dense curve per device over circuit
// depth n (x = depth, y = fidelity, z = series index), coloured by fidelity, and
// mark the engine's computed 10-gate point. Hundreds of points per device.
function set3DFidelity(rows, title) {
  try {
    if (!Array.isArray(rows) || !rows.length) return;
    const points = [], values = [];
    const NMAX = 60;            // circuit depth sweep
    for (let s = 0; s < rows.length; s++) {
      const x = rows[s];
      // Recover the per-gate fidelity that the engine used:
      // circuit_depth_10 = single_f^10 * readout_f  ->  single_f known directly.
      const sf = Number(x.single_gate_fidelity);
      const rf = Number(x.readout_fidelity);
      if (!isFinite(sf) || !isFinite(rf)) continue;
      for (let n = 0; n <= NMAX; n++) {
        const f = Math.pow(sf, n) * rf;
        points.push(n, f, s);
        values.push(f);
      }
    }
    if (!points.length) return setPayload(null, null, title);
    setPayload(points, values, title + ' - circuit fidelity F(depth)');
  } catch (_) {}
}

// Error correction: p_L = 0.1 * (p/p_th)^((d+1)/2) is the closed-form logical
// error model. Render it as a DENSE surface over (distance d, physical error
// rate p), z = log10(p_L), coloured by log10(p_L); then overlay the engine's
// computed codes as marked points. Thousands of surface points.
function set3DErrorCorrect(rows, title) {
  try {
    const points = [], values = [];
    const p_th = 0.01;
    const D0 = 3, D1 = 25;                 // distance grid
    const P0 = 0.0005, P1 = 0.05, NP = 60; // physical error rate grid (log)
    for (let d = D0; d <= D1; d += 2) {
      const exponent = (d + 1) / 2.0;
      for (let j = 0; j <= NP; j++) {
        const t = j / NP;
        const p = P0 * Math.pow(P1 / P0, t); // log-spaced
        const pL = 0.1 * Math.pow(p / p_th, exponent);
        const z = pL > 0 ? Math.log10(pL) : -40;
        points.push(d, p, z);
        values.push(z);
      }
    }
    // Mark the actually-computed codes on the surface.
    if (Array.isArray(rows)) {
      for (const x of rows) {
        const d = Number(x.distance);
        const pL = Number(x.logical_error_rate);
        if (!isFinite(d) || !isFinite(pL)) continue;
        const exponent = (d + 1) / 2.0;
        // recover p from p_L: p = p_th * (pL/0.1)^(1/exponent)
        const p = p_th * Math.pow(pL / 0.1, 1.0 / exponent);
        const z = pL > 0 ? Math.log10(pL) : -40;
        points.push(d, p, z);
        values.push(z);
      }
    }
    if (!points.length) return;
    setPayload(points, values, title + ' - p_L surface over (d, p)');
  } catch (_) {}
}

/* Entanglement: CHSH derives from the genuine Bell correlation E(theta) = -v cos(2 theta).
   For each pair, render the quantum correlation curve E(a,b) sampled over the
   relative analyzer angle. x = angle, y = correlation, z = series index,
   coloured by correlation. Hundreds of points per pair.

   THE AMPLITUDE IS THE WERNER VISIBILITY, NOT THE FIDELITY. The engine models
   a depolarized Bell pair, rho(v) = v |Psi><Psi| + (1 - v) I/4, whose
   correlation function is E(theta) = -v cos(2 theta) with amplitude v. The
   reported bell_fidelity is F = (1 + 3v)/4, which is strictly LARGER than v for
   every v < 1, so plotting F as the amplitude drew a curve that crosses the
   CHSH bound at visibilities where no violation exists. Invert the published
   Werner relation to recover v; see Horodecki, Horodecki & Horodecki, Phys.
   Lett. A 200 (1995) 340, which fixes the violation threshold at v > 1/sqrt(2)
   (equivalently F > 0.7803), not at F > 1/sqrt(2). */
function set3DEntanglement(rows, title) {
  try {
    if (!Array.isArray(rows) || !rows.length) return;
    const points = [], values = [];
    const NA = 90; // angle samples over [0, pi]
    for (let s = 0; s < rows.length; s++) {
      const x = rows[s];
      const fBell = Number(x.bell_fidelity);
      let v = 1.0;
      if (isFinite(fBell)) {
        // F = (1 + 3v)/4 inverted. Clamped because a Werner visibility outside
        // [0, 1] is not a state, and the viewport should not be asked to draw one.
        const inverted = (4.0 * fBell - 1.0) / 3.0;
        v = Math.min(1.0, Math.max(0.0, inverted));
      }
      for (let k = 0; k <= NA; k++) {
        const theta = Math.PI * (k / NA);
        const e = -v * Math.cos(2.0 * theta); // singlet correlation, visibility v
        points.push(theta, e, s);
        values.push(e);
      }
    }
    if (!points.length) return;
    setPayload(points, values, title + ' - Bell correlation E(theta)');
  } catch (_) {}
}

// Decoherence: T1, T2 are genuine relaxation/dephasing times. Render the
// coherence envelopes exp(-t/T1) (relaxation) and exp(-t/T2) (Ramsey) densely
// over real time per qubit. x = time (us), y = amplitude, z = qubit index,
// coloured by amplitude. Two dense curves per qubit, hundreds of points each.
function set3DDecoherence(rows, title) {
  try {
    if (!Array.isArray(rows) || !rows.length) return;
    const points = [], values = [];
    const NT = 80;
    // common time horizon = 3x the largest T1 so every curve is resolved
    let tmax = 0;
    for (const x of rows) { const t1 = Number(x.t1_us); if (isFinite(t1)) tmax = Math.max(tmax, t1); }
    if (!(tmax > 0)) tmax = 100;
    tmax *= 3;
    for (let s = 0; s < rows.length; s++) {
      const x = rows[s];
      const t1 = Number(x.t1_us), t2 = Number(x.t2_us);
      if (!isFinite(t1) || !isFinite(t2)) continue;
      for (let k = 0; k <= NT; k++) {
        const t = tmax * (k / NT);
        const a1 = Math.exp(-t / t1);     // population relaxation
        points.push(t, a1, s);
        values.push(a1);
        const a2 = Math.exp(-t / t2);     // coherence (Ramsey) decay
        points.push(t, a2, s + 0.5);      // offset series so both are visible
        values.push(a2);
      }
    }
    if (!points.length) return;
    setPayload(points, values, title + ' - coherence envelopes exp(-t/T)');
  } catch (_) {}
}

// Circuit: per-hardware circuit success probability P(depth) = (1 - error_budget
// per layer)^depth is the genuine reachability model. Render P(d) densely over
// circuit depth per device and mark the engine's computed max_depth. x = depth,
// y = success probability, z = series index. Hundreds of points per device.
function set3DCircuit(rows, title) {
  try {
    if (!Array.isArray(rows) || !rows.length) return;
    const points = [], values = [];
    // depth horizon = 1.5x the deepest computed circuit so the marker sits on-curve
    let dmax = 0;
    for (const x of rows) { const md = Number(x.max_depth); if (isFinite(md)) dmax = Math.max(dmax, md); }
    if (!(dmax > 0)) dmax = 100;
    dmax = Math.ceil(dmax * 1.5);
    const ND = 80;
    for (let s = 0; s < rows.length; s++) {
      const x = rows[s];
      const md = Number(x.max_depth);
      const eb = Number(x.error_budget);
      if (!isFinite(md) || md <= 0) continue;
      // per-layer error so that the budget is consumed exactly at max_depth:
      // (1-perr)^max_depth = 1 - error_budget  ->  perr = 1 - (1-eb)^(1/md)
      const target = isFinite(eb) && eb > 0 && eb < 1 ? (1 - eb) : (1 / Math.E);
      const perr = 1 - Math.pow(target, 1.0 / md);
      for (let k = 0; k <= ND; k++) {
        const d = dmax * (k / ND);
        const psucc = Math.pow(1 - perr, d);
        points.push(d, psucc, s);
        values.push(psucc);
      }
      // mark the computed max_depth point explicitly
      points.push(md, Math.pow(1 - perr, md), s);
      values.push(Math.pow(1 - perr, md));
    }
    if (!points.length) return;
    setPayload(points, values, title + ' - success probability P(depth)');
  } catch (_) {}
}

function add3DButton(panelId, statusId) {
  try {
    const host = document.getElementById('s-' + statusId);
    if (!host || !host.parentNode) return;
    const existing = host.parentNode.querySelector('.q3d-btn');
    if (existing) return;
    const btn = document.createElement('button');
    btn.className = 'q3d-btn';
    btn.textContent = 'View in 3D';
    btn.style.cssText = 'margin-left:8px;font-size:11px;padding:2px 8px;';
    btn.addEventListener('click', () => {
      if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
    });
    host.parentNode.insertBefore(btn, host.nextSibling);
  } catch (_) {}
}

// GeoNum precision demonstration for quantum calculations
function geoNumDemo(value, description) {
  const ieee = value;
  const geoVal = geo(value);
  const geoResult = geoVal.toFloat64();
  const uncertainty = geoVal.getUncertainty();
  const relUncertainty = geoVal.getRelativeUncertainty();

  return `<div class="geonum-result">
    <div class="geonum-label">${description}</div>
    <div class="geonum-comparison">
      <div class="geonum-ieee">IEEE 754: ${ieee.toExponential(8)}</div>
      <div class="geonum-geo">GeoNum: ${geoResult.toExponential(8)} ± ${uncertainty.toExponential(2)}</div>
      <div class="geonum-meta">Zone: ${geoVal.zone} | Shade: ${geoVal.shade} | Drift: ${geoVal.drift.toFixed(4)} | Rel. Unc: ${(relUncertainty * 100).toExponential(2)}%</div>
    </div>
  </div>`;
}

// --- Scans ---

function fidelityScan() {
  status('fidelity', 'Computing...'); const qubits = JSON.parse(document.getElementById('in-fidelity').value);
  const res = JSON.parse(run_fidelity_scan(JSON.stringify({ qubits, precision: 0 })));
  const r = res.results;
  renderTable('fidelity', ['Name', 'F_1Q', 'F_2Q', 'F_readout', 'F_10gates', 'T2/T1', 'Coherence'], r.map(x => [x.name, x.single_gate_fidelity, x.two_qubit_fidelity, x.readout_fidelity, x.circuit_depth_10, x.t2_t1_ratio, renderGradientBar(x.tiered_coherence)]));
  makeChart('fidelity', r.map(x => x.name), [
    { label: '1-Qubit F', data: r.map(x => x.single_gate_fidelity), backgroundColor: '#ce93d8' },
    { label: '2-Qubit F', data: r.map(x => x.two_qubit_fidelity), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
  ], 'Qubit', 'Fidelity');

  renderQuantumDeviceRefNote('quantum-ref-fidelity');

  // GeoNum precision demo using first qubit's fidelities
  if (r.length > 0) {
    const firstQubit = r[0];
    const geoDemo = geoNumDemo(firstQubit.single_gate_fidelity, 'Single-Gate Fidelity')
      + geoNumDemo(firstQubit.two_qubit_fidelity, 'Two-Qubit Fidelity')
      + geoNumDemo(firstQubit.circuit_depth_10, 'Circuit Depth 10 Fidelity');
    const demoEl = document.getElementById('geonum-demo-fidelity');
    if (demoEl) demoEl.innerHTML = '<h4>GeoNum Precision Tracking</h4>' + geoDemo;
  }

  status('fidelity', `Done — ${r.length} qubits`);
  animData.fidelity = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('qtp-fidelity', 'qua-fidelity', () => animData.fidelity, 'Quantum Fidelity');
  set3DFidelity(r, 'Quantum Fidelity');
  add3DButton('qtp-fidelity', 'fidelity');
}

function errorcorrectScan() {
  status('errorcorrect', 'Computing...'); const codes = JSON.parse(document.getElementById('in-errorcorrect').value);
  const res = JSON.parse(run_errorcorrection_scan(JSON.stringify({ codes, precision: 0 })));
  const r = res.results;
  renderTable('errorcorrect', ['Name', 'd', 'Phys Qubits', 'p_L', 'Suppression', 'Below \u03B8?', 'Coherence'], r.map(x => [x.name, x.distance, x.physical_qubits, x.logical_error_rate, x.suppression_factor, x.below_threshold ? 'Yes' : 'No', renderGradientBar(x.tiered_coherence)]));
  makeChart('errorcorrect', r.map(x => x.name), [
    { label: 'log\u2081\u2080(p_L)', data: r.map(x => x.logical_error_rate > 0 ? Math.log10(x.logical_error_rate) : -20), backgroundColor: '#ce93d8' },
  ], 'Code', 'log\u2081\u2080(p_L)');
  status('errorcorrect', `Done — ${r.length} codes`);
  animData.errorcorrect = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('qtp-errorcorrect', 'qua-errorcorrect', () => animData.errorcorrect, 'Error Correction');
  set3DErrorCorrect(r, 'Error Correction');
  add3DButton('qtp-errorcorrect', 'errorcorrect');
}

function entanglementScan() {
  status('entanglement', 'Computing...'); const pairs = JSON.parse(document.getElementById('in-entanglement').value);
  const res = JSON.parse(run_entanglement_scan(JSON.stringify({ pairs, precision: 0 })));
  const r = res.results;
  /* Surface the visibility next to the fidelity, and say plainly whether the
     Horodecki condition is met. The two numbers were previously conflated, and
     a reader who sees only F next to S has no way to tell which one the S was
     built from. v is recovered from the published Werner relation F = (1+3v)/4;
     the violation call is the engine's own S against the classical bound, which
     is equivalent to v > 1/sqrt(2). */
  const wernerVisibility = (fBell) => {
    const f = Number(fBell);
    if (!isFinite(f)) return NaN;
    return Math.min(1.0, Math.max(0.0, (4.0 * f - 1.0) / 3.0));
  };
  const bellVerdict = (chsh) => {
    const s = Number(chsh);
    if (!isFinite(s)) return 'n/a';
    if (s > QUANTUM_REFS.chsh.classical_limit) return 'violates CHSH';
    return 'no violation';
  };
  renderTable('entanglement',
    ['Name', 'F_Bell', 'Visibility v', 'Concurrence', 'CHSH', 'Bell test', 'Rate (MHz)', 'Coherence'],
    r.map(x => [x.name, x.bell_fidelity, wernerVisibility(x.bell_fidelity), x.concurrence,
                x.chsh_value, bellVerdict(x.chsh_value), x.entanglement_rate,
                renderGradientBar(x.tiered_coherence)]));
  makeChart('entanglement', r.map(x => x.name), [
    { label: 'CHSH', data: r.map(x => x.chsh_value), backgroundColor: '#ce93d8' },
    { label: 'Concurrence', data: r.map(x => x.concurrence), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
    { label: 'Classical limit (S=2.0)', data: r.map(() => 2.0), type: 'line', borderColor: '#888', borderDash: [5, 5], fill: false, pointRadius: 0, order: 0 },
    { label: 'Tsirelson bound (2\u221A2\u22482.828)', data: r.map(() => 2.828), type: 'line', borderColor: '#a78bfa', borderDash: [5, 5], fill: false, pointRadius: 0, order: 0 },
  ], 'Pair', 'Value');
  renderCHSHRefNote('quantum-ref-entanglement');
  status('entanglement', `Done — ${r.length} pairs`);
  animData.entanglement = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('qtp-entanglement', 'qua-entanglement', () => animData.entanglement, 'Quantum Entanglement');
  set3DEntanglement(r, 'Quantum Entanglement');
  add3DButton('qtp-entanglement', 'entanglement');
}

function decoherenceScan() {
  status('decoherence', 'Computing...'); const qubits = JSON.parse(document.getElementById('in-decoherence').value);
  const res = JSON.parse(run_decoherence_scan(JSON.stringify({ qubits, precision: 0 })));
  const r = res.results;
  renderTable('decoherence', ['Name', 'T1 (\u03BCs)', 'T2 (\u03BCs)', 'T\u03C6 (\u03BCs)', 'n_th', 'DD \u00D7', 'Coherence'], r.map(x => [x.name, x.t1_us, x.t2_us, x.t_phi_us, x.thermal_population, x.dd_improvement, renderGradientBar(x.tiered_coherence)]));
  makeChart('decoherence', r.map(x => x.name), [
    { label: 'T1 (\u03BCs)', data: r.map(x => x.t1_us), backgroundColor: '#ce93d8' },
    { label: 'T2 (\u03BCs)', data: r.map(x => x.t2_us), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
  ], 'Qubit', 'Time (\u03BCs)');
  renderQuantumDeviceRefNote('quantum-ref-decoherence');
  status('decoherence', `Done — ${r.length} qubits`);
  animData.decoherence = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('qtp-decoherence', 'qua-decoherence', () => animData.decoherence, 'Decoherence');
  set3DDecoherence(r, 'Decoherence');
  add3DButton('qtp-decoherence', 'decoherence');
}

function circuitScan() {
  status('circuit', 'Computing...'); const hardware = JSON.parse(document.getElementById('in-circuit').value);
  const res = JSON.parse(run_circuit_scan(JSON.stringify({ hardware, precision: 0 })));
  const r = res.results;
  renderTable('circuit', ['Name', 'Max Depth', 'QV', 'SWAP OH', 'Error Budget', 'Coherence'], r.map(x => [x.name, x.max_depth, x.quantum_volume, x.swap_overhead, x.error_budget, renderGradientBar(x.tiered_coherence)]));
  makeChart('circuit', r.map(x => x.name), [
    { label: 'log\u2082(QV)', data: r.map(x => Math.log2(x.quantum_volume)), backgroundColor: '#ce93d8' },
    { label: 'Max Depth /100', data: r.map(x => x.max_depth / 100), backgroundColor: 'rgba(79,195,247,0.6)', borderColor: '#4fc3f7', borderWidth: 2 },
  ], 'Hardware', 'Value');
  renderQuantumDeviceRefNote('quantum-ref-circuit');
  status('circuit', `Done — ${r.length} hardware`);
  animData.circuit = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('qtp-circuit', 'qua-circuit', () => animData.circuit, 'Circuit Analysis');
  set3DCircuit(r, 'Circuit Analysis');
  add3DButton('qtp-circuit', 'circuit');
}
